/**
 * useNodeOperationInfo Hook
 *
 * Resolves operation information (connectorId, operationId) from node data.
 * Handles multiple scenarios:
 * 1. Built-in operations - uses local manifests (no network fetch)
 * 2. Managed connectors (ApiConnection) - resolves apiId from connection store
 * 3. Service Provider operations - extracts from serviceProviderConfiguration
 * 4. Legacy tools - falls back to path/method when operationId is missing
 *
 * This centralizes the complex operation info resolution logic that was
 * previously spread across ParameterList.tsx.
 */

import { useMemo } from 'react';
import { useConnectionStore } from '@/stores/connectionStore';
import { isBuiltInOperation } from '@/data/manifests';
import { extractConnectorName, isApiConnectionType } from '@/utils/connections';
import { telemetryService } from '@/services/telemetryService';
import type { OperationInfo } from '@/types/operationManifest';
import type { Action, Trigger } from '@/types/workflowDefinition';
import { isCodingAgentAction, isFoundryAgentAction } from '@/types/workflowDefinition';

/**
 * Map node types to their connector IDs for runtime API manifest fetching.
 * These are built-in operations that need to fetch manifests from the runtime API.
 * Keep aligned with the operations actually shipped by Otto edge — backend
 * exposure is gated by `Is*Enabled` flags in `BuiltInApiOperationsDataProvider`,
 * so a type being declared there is not enough; only add it here once the
 * corresponding flag is on for our deployment.
 */
const NODE_TYPE_TO_CONNECTOR_ID: Record<string, string> = {
  // XML operations (IsXmlOperationsEnabled)
  'Xslt': 'connectionProviders/xmlOperations',
  'XmlValidation': 'connectionProviders/xmlOperations',
  'XmlTransform': 'connectionProviders/xmlOperations',
  'XmlCompose': 'connectionProviders/xmlOperations',
  'XmlParse': 'connectionProviders/xmlOperations',
  // Flat file operations (IsFlatFileOperationsEnabled)
  'FlatFileDecoding': 'connectionProviders/flatFileOperations',
  'FlatFileEncoding': 'connectionProviders/flatFileOperations',
  // Inline code
  'JavaScriptCode': 'connectionProviders/inlineCode',
  // Data operations
  'Compose': 'connectionProviders/dataOperationNew',
  'ParseJson': 'connectionProviders/dataOperationNew',
  'Query': 'connectionProviders/dataOperationNew',
  'Select': 'connectionProviders/dataOperationNew',
  'Join': 'connectionProviders/dataOperationNew',
  'Table': 'connectionProviders/dataOperationNew',
  // AI operations (ParseDocument is intentionally absent — Otto edge only ships ParseDocumentWithMetadata)
  'ChunkText': 'connectionProviders/aiOperations',
  'ChunkTextWithMetadata': 'connectionProviders/aiOperations',
  'ParseDocumentWithMetadata': 'connectionProviders/aiOperations',
  // Batch (IsBatchOperationsEnabled)
  'Batch': '/connectionProviders/batch',
  'SendToBatch': '/connectionProviders/batch',
};

/**
 * Overrides for node types whose backend operationId differs from the type name
 * even under case-insensitive comparison. Types absent from this map use the
 * node type itself as the operationId.
 */
const NODE_TYPE_TO_OPERATION_ID: Record<string, string> = {
  Xslt: 'xmlTransform',
};

/**
 * Resolve the runtime operationId for a literal-type built-in node.
 */
function getRuntimeOperationId(nodeType: string): string {
  return NODE_TYPE_TO_OPERATION_ID[nodeType] ?? nodeType;
}

/**
 * Get the connector ID for a node type.
 * Returns a connectionProviders path for runtime operations,
 * or the nodeType itself for built-in operations.
 */
function getConnectorIdForNodeType(nodeType: string): string {
  return NODE_TYPE_TO_CONNECTOR_ID[nodeType] ?? `connectionProviders/${nodeType.toLowerCase()}`;
}

/**
 * Service Provider configuration structure
 */
interface ServiceProviderConfig {
  serviceProviderId?: string;
  operationId?: string;
  connectionName?: string;
}

/**
 * Input host structure for ApiConnection actions
 */
interface InputHost {
  apiId?: string;
  operationId?: string;
  connection?: {
    referenceName?: string;
  };
}

/**
 * Node inputs with typed fields
 */
interface NodeInputs {
  host?: InputHost;
  path?: string;
  method?: string;
  serviceProviderConfiguration?: ServiceProviderConfig;
  [key: string]: unknown;
}

export interface UseNodeOperationInfoOptions {
  /** The node data (action or trigger) */
  nodeData: Action | Trigger | null | undefined;
  /** Parameter values from the node editor (for explicit _apiId/_operationId overrides) */
  parameterValues?: Record<string, unknown>;
}

export interface NodeOperationInfoResult {
  /** The resolved operation info for manifest lookup */
  operationInfo: OperationInfo | null;
  /** The connector/API ID extracted from the node */
  apiId: string | undefined;
  /** The operation ID extracted from the node */
  operationId: string | undefined;
  /** The path for legacy tools without operationId */
  path: string | undefined;
  /** The HTTP method for legacy tools without operationId */
  method: string | undefined;
  /** Service provider configuration if present */
  serviceProviderConfig: ServiceProviderConfig | undefined;
}

/**
 * Hook to resolve operation information from node data.
 *
 * This handles the complex logic of extracting operationId and connectorId
 * from various node structures:
 *
 * 1. Built-in operations: Uses local manifests (Agent, Response, etc.)
 * 2. Explicit overrides: Uses _apiId/_operationId from parameterValues
 * 3. ApiConnection tools: Uses nodeData.operationId + inputs.host.apiId
 * 4. Legacy tools: Falls back to connection store lookup + path/method
 * 5. Service providers: Uses serviceProviderConfiguration from inputs
 * 6. Runtime operations: Uses nodeType to construct connectorId
 */
export function useNodeOperationInfo({
  nodeData,
  parameterValues = {},
}: UseNodeOperationInfoOptions): NodeOperationInfoResult {
  const connectionReferences = useConnectionStore((state) => state.references);

  // Extract basic node properties
  const nodeType = nodeData?.type ?? '';
  const nodeKind = (nodeData as { kind?: string })?.kind;
  const nodeInputs = (nodeData as { inputs?: NodeInputs })?.inputs;
  // operationId can live at the top of the action (Logic Apps legacy shape) or
  // inside `inputs.host.operationId` (current shape produced by serializeHost
  // for ApiConnection / ApiConnectionWebhook). Without the host fallback,
  // agent-tool ApiConnectionWebhook actions resolve to an empty operationId,
  // leaving the manifest query disabled and the parameter list stuck on the
  // loading spinner.
  const nodeOperationId = (nodeData as { operationId?: string })?.operationId
    ?? nodeInputs?.host?.operationId;

  // Extract apiId from inputs.host, with fallback to connection store lookup
  const apiId = useMemo(() => {
    // First check parameterValues for explicit _apiId override
    const explicitApiId = parameterValues['_apiId'] as string | undefined;
    if (explicitApiId) {
      return explicitApiId;
    }

    // Get the connection reference name from either:
    // 1. parameterValues['_connection'] (current editor state - takes priority)
    // 2. nodeData inputs.host.connection.referenceName (original saved state)
    const connectionFromParams = parameterValues['_connection'] as string | undefined;
    const host = nodeInputs?.host;
    const connectionFromInputs = host?.connection?.referenceName;
    const referenceName = connectionFromParams ?? connectionFromInputs;

    // If we have a connection reference, look up the apiId from connection store
    if (referenceName && connectionReferences[referenceName]) {
      const apiId = connectionReferences[referenceName].api?.id;
      if (apiId) {
        return apiId;
      }
    }

    // Fall back to direct apiId from host (for legacy tools).
    // Reject malformed values (e.g. "/automationProjects/foo/managedApis/" with an empty
    // connector tail) — they poison downstream manifest fetches and the connection
    // picker, which derive the connector name from this id. When the tail is empty
    // we let resolution fall through to the connection-name fallback below.
    if (host?.apiId && extractConnectorName(host.apiId)) {
      return host.apiId;
    }

    // No usable apiId for this ApiConnection action. Emit telemetry so we
    // can spot connections.json drift in production and return undefined;
    // the operationInfo memo and useOperationManifest already render a
    // "select a connection" state when the connector is unknown.
    if (isApiConnectionType(nodeType) && referenceName) {
      telemetryService.trackEvent('connection.connectorId.unresolvable', {
        callSite: 'useNodeOperationInfo',
        nodeType,
        // Distinguishes "host.apiId truly absent" from "present but malformed".
        hostApiIdPresent: typeof host?.apiId === 'string' && host.apiId.length > 0,
        referenceInStore: !!connectionReferences[referenceName],
      });
    }

    return undefined;
  }, [nodeInputs, nodeType, connectionReferences, parameterValues]);


  // Extract path for legacy tools
  const path = useMemo(() => {
    if (!nodeInputs || typeof nodeInputs !== 'object') return undefined;
    return nodeInputs.path;
  }, [nodeInputs]);

  // Extract HTTP method for legacy tools
  const method = useMemo(() => {
    if (!nodeInputs || typeof nodeInputs !== 'object') return undefined;
    return nodeInputs.method;
  }, [nodeInputs]);

  // Extract Service Provider configuration
  const serviceProviderConfig = useMemo((): ServiceProviderConfig | undefined => {
    if (nodeType !== 'ServiceProvider' || !nodeInputs) return undefined;
    return nodeInputs.serviceProviderConfiguration;
  }, [nodeType, nodeInputs]);

  // Construct the final OperationInfo
  const operationInfo = useMemo((): OperationInfo | null => {
    // 0. Foundry agents share the 'Agent' node type with native agent-loop but
    //    use a different manifest. Detect via agentModelType and route here so
    //    every consumer (manifest fetch, parameter editor, dynamic-data) sees
    //    the Foundry-specific schema.
    if (nodeType === 'Agent' && nodeData && isFoundryAgentAction(nodeData as Action)) {
      return { connectorId: 'FoundryAgent', operationId: 'FoundryAgent' };
    }

    // Coding agents are also `type: 'Agent'` but carry an `agentHarness` block
    // under `agentModelSettings`. Route to the dedicated manifest so the
    // sandbox-specific schema and `fileOutputs` are surfaced.
    if (nodeType === 'Agent' && nodeData && isCodingAgentAction(nodeData as Action)) {
      return { connectorId: 'CodingAgent', operationId: 'CodingAgent' };
    }

    // 1. First check for built-in operations with local manifests (no network fetch needed)
    if (nodeType && isBuiltInOperation(nodeType, nodeKind)) {
      return {
        connectorId: nodeType,
        operationId: nodeType,
      };
    }

    // Also check if serviceProviderConfig.operationId is a built-in operation
    if (serviceProviderConfig?.operationId && isBuiltInOperation(serviceProviderConfig.operationId)) {
      return {
        connectorId: serviceProviderConfig.operationId,
        operationId: serviceProviderConfig.operationId,
      };
    }

    // 2. Try explicit connector/operation IDs (for managed connectors selected via operation picker)
    const explicitApiId = parameterValues['_apiId'] as string | undefined;
    const explicitOperationId = parameterValues['_operationId'] as string | undefined;

    // Check if the explicit operationId itself is a built-in operation
    if (explicitOperationId && isBuiltInOperation(explicitOperationId)) {
      return {
        connectorId: explicitOperationId,
        operationId: explicitOperationId,
      };
    }

    if (explicitApiId && explicitOperationId) {
      return { connectorId: explicitApiId, operationId: explicitOperationId };
    }

    // 2b. Fallback: Use computed apiId with explicit operationId
    // This handles notification triggers where apiId is resolved from connection but operationId is in metadata
    if (apiId && explicitOperationId) {
      return { connectorId: apiId, operationId: explicitOperationId };
    }

    // 3. Check for nodeOperationId (for tools with connector operations like ApiConnection)
    if (nodeOperationId && isBuiltInOperation(nodeOperationId)) {
      return {
        connectorId: nodeOperationId,
        operationId: nodeOperationId,
      };
    }

    if (apiId && nodeOperationId) {
      return { connectorId: apiId, operationId: nodeOperationId };
    }

    // 4. For legacy tools with apiId but no operationId, use path/method as fallback
    if (apiId && !nodeOperationId && path && method) {
      return {
        connectorId: apiId,
        operationId: '', // Empty - will use path/method fallback in useOperationManifest
        path,
        method,
      };
    }

    // 4b. For notification triggers: return connectorId even without operationId
    // The operationId will be resolved from the fetch path using the swagger
    // We need to return something so the swagger gets loaded
    if (apiId && (nodeType === 'ApiConnectionNotification' || nodeType === 'ApiConnectionWebhook')) {
      return {
        connectorId: apiId,
        operationId: '', // Will be resolved from fetch path in useOperationManifestData
      };
    }

    // 5. For Service Provider operations, extract from serviceProviderConfiguration
    if (serviceProviderConfig?.serviceProviderId && serviceProviderConfig?.operationId) {
      return {
        connectorId: serviceProviderConfig.serviceProviderId,
        operationId: serviceProviderConfig.operationId,
      };
    }

    // 6. For operations with operationId but no apiId, construct connectorId from nodeType
    const effectiveOperationId = explicitOperationId ?? nodeOperationId;
    if (effectiveOperationId && nodeType) {
      const connectorId = getConnectorIdForNodeType(nodeType);
      return { connectorId, operationId: effectiveOperationId };
    }

    // 7. Runtime literal-type built-ins (e.g. ChunkText, Xslt) store only `type`
    // in the workflow — no operationId, no host, no SP config. Derive the
    // operationId from the type so the runtime manifest fetch can resolve it.
    // Only fires for explicitly mapped types; unmapped types would 404.
    if (nodeType && NODE_TYPE_TO_CONNECTOR_ID[nodeType]) {
      return {
        connectorId: NODE_TYPE_TO_CONNECTOR_ID[nodeType],
        operationId: getRuntimeOperationId(nodeType),
      };
    }

    return null;
  }, [
    nodeType,
    nodeKind,
    nodeData,
    serviceProviderConfig,
    parameterValues,
    nodeOperationId,
    apiId,
    path,
    method,
  ]);

  return {
    operationInfo,
    apiId,
    operationId: nodeOperationId,
    path,
    method,
    serviceProviderConfig,
  };
}
